import { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../services/api.service';

export interface TransactionsReportFilters {
  currency: string; // 'all' | currency code (e.g. 'USD')
  dateFrom: string;
  dateTo: string;
  minAmount: string;
  maxAmount: string;
  category: string; // 'all' | category documentId
  branch: string; // 'all' | branch documentId
  search: string;
}

export interface TransactionsReportSummary {
  totalTransactions: number;
  totalAmount: number; // EGP equivalent (backend converts non-EGP with the rate)
  byCurrency: Record<string, { count: number; total: number }>;
}

/**
 * Server-side filtered transactions list + totals (GET /api/transactions/report).
 * When the endpoint isn't available yet, `available` turns false and the list
 * page falls back to client-side filtering (without the totals cards).
 */
export function useTransactionsReport(type: 'in' | 'out', f: TransactionsReportFilters) {
  const [summary, setSummary] = useState<TransactionsReportSummary | null>(null);
  const [results, setResults] = useState<any[]>([]);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ type });
      if (f.currency && f.currency !== 'all') params.set('currency', f.currency);
      if (f.dateFrom) params.set('dateFrom', f.dateFrom);
      if (f.dateTo) params.set('dateTo', f.dateTo);
      if (f.minAmount) params.set('minAmount', f.minAmount);
      if (f.maxAmount) params.set('maxAmount', f.maxAmount);
      if (f.category && f.category !== 'all') params.set('category', f.category);
      if (f.branch && f.branch !== 'all') params.set('branch', f.branch);
      if (f.search) params.set('search', f.search);
      const res: any = await apiService.get(`/transactions/report?${params.toString()}`);
      setSummary(res?.summary || null);
      setResults(Array.isArray(res?.results) ? res.results : []);
      setAvailable(true);
    } catch {
      setAvailable(false);
    } finally {
      setIsLoading(false);
    }
  }, [type, f.currency, f.dateFrom, f.dateTo, f.minAmount, f.maxAmount, f.category, f.branch, f.search]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return { summary, results, available, isLoading, refetch: fetchReport };
}
